import { IMAGES } from "../assets";

//COMPONENTS
import {
  LATextInput,
  LAButton,
  LAView,
  LAText,
} from "../components";

//CONSTANTS
import { STRINGS } from "../constant";

//PACKAGES
import { useNavigate } from "react-router-dom";

import { ErrorMessage, Formik } from "formik";
import * as Yup from "yup";
import { emailRegex } from "../constant/utils";
import { useState } from "react";
import { auth } from "../api/config";
import { SHOW_TOAST } from "../constant/utils";
import { useAnalytics } from "../context/AnalyticsProvider";

function ForgotPassword() {
  const [isLoading, setLoading] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const navigation = useNavigate();
  const { trackPasswordReset, trackPageView, trackNavigation } = useAnalytics();

  const handleSubmit = async (values, { resetForm }) => {
    setLoading(true);
    try {
      await auth().sendPasswordResetEmail(values.email);
      setLoading(false);
      
      // Track successful password reset request
      await trackPasswordReset({
        email: values.email,
        reset_success: true
      });
      
      setDialogOpen(true);
      resetForm();
    } catch (error) {
      setLoading(false);
      
      // Track failed password reset request
      await trackPasswordReset({
        email: values.email,
        reset_success: false,
        error_message: error.message
      });
      
      SHOW_TOAST(error.message, "error");
    }
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required("Please enter the email")
      .matches(emailRegex, "Invalid email address"),
  });

  // Track forgot password page view
  useState(() => {
    trackPageView('/forgot-password');
  }, []);

  const handleBackToLogin = () => {
    trackNavigation('forgot-password', 'login');
    setDialogOpen(false);
    navigation("/login");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary dark:bg-gray-800">
      <div className="w-full max-w-md p-8 space-y-8 bg-white dark:bg-gray-900 rounded-lg shadow-lg">
        <div className="text-center mb-8">
          <img
            src={IMAGES.logo_prepare}
            alt="LearnZapp"
            className="w-1/2 mx-auto"
          />
          <h1 className="text-2xl mt-4 dark:text-white">Reset Password</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Enter your email to receive a password reset link
          </p>
        </div>
        <Formik
          initialValues={{ email: "" }}
          enableReinitialize
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({
            values,
            errors,
            handleSubmit,
            handleBlur,
            handleChange,
            resetForm,
          }) => {
            return (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <div className="relative w-full">
                    <img src={IMAGES.ic_email} alt="Email Icon" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" />
                    <input
                      type="email"
                      name="email"
                      id="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values?.email}
                      placeholder="Email address"
                      className="w-full pl-10 pr-4 py-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    />
                  </div>
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="text-xs text-rose-500 mt-1"
                  />
                </div>
                <div>
                  <button
                    type="submit"
                    className="w-full text-white py-4 rounded-lg bg-primary hover:bg-accent transition dark:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-900"
                  >
                    {STRINGS.reset}
                  </button>
                </div>
              </form>
            );
          }}
        </Formik>
      </div>

      {isDialogOpen && (
        <LADialog
          isOpen={isDialogOpen}
          onClose={handleBackToLogin}
          title={STRINGS.reset_popup_title}
          description={STRINGS.reset_popup_message}
          buttons={[
            {
              text: "OK",
              onClick: handleBackToLogin,
            },
          ]}
        />
      )}
    </div>
  );
}

function LADialog({ isOpen, onClose, title, description, buttons }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4">
        <h2 className="text-xl font-semibold mb-4 dark:text-white">{title}</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">{description}</p>
        <div className="flex justify-end space-x-3">
          {buttons.map((button, index) => (
            <button
              key={index}
              onClick={button.onClick}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
            >
              {button.text}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
